import type {TimelineItemData} from '../timeline.types';
import {MIN_ITEM_DURATION} from '../timeline.types';
import {formatSecondsToTimeString} from '@Utils/date.util';

export const timeToPixels = (time: number, pixelsPerSecond: number): number => {
  return time * pixelsPerSecond;
};

export const pixelsToTime = (pixels: number, pixelsPerSecond: number): number => {
  return pixels / pixelsPerSecond;
};

export const getItemWidth = (startTime: number, endTime: number, pixelsPerSecond: number): number => {
  return timeToPixels(endTime - startTime, pixelsPerSecond);
};

export const getItemLeft = (startTime: number, pixelsPerSecond: number): number => {
  return timeToPixels(startTime, pixelsPerSecond);
};

export const detectOverlap = (item1: TimelineItemData, item2: TimelineItemData): boolean => {
  if (item1.id === item2.id) {
    return false;
  }
  return item1.startTime < item2.endTime && item1.endTime > item2.startTime;
};

export const assignSubRows = (items: TimelineItemData[]): TimelineItemData[] => {
  if (items.length === 0) {
    return [];
  }

  const sortedItems = [...items].sort((a, b) => {
    if (a.subRow !== b.subRow) {
      return a.subRow - b.subRow;
    }
    return a.startTime - b.startTime;
  });

  // Use a Map from row index → items to avoid O(n) filter scans on every iteration
  const rowMap = new Map<number, TimelineItemData[]>();
  const result: TimelineItemData[] = [];

  for (const item of sortedItems) {
    let assignedRow = item.subRow;

    const savedRowItems = rowMap.get(assignedRow) ?? [];
    const hasOverlapInSavedRow = savedRowItems.some((existing) => detectOverlap(item, existing));

    if (hasOverlapInSavedRow) {
      assignedRow = 0;
      while ((rowMap.get(assignedRow) ?? []).some((existing) => detectOverlap(item, existing))) {
        assignedRow += 1;
      }
    }

    const finalItem = {...item, subRow: assignedRow};
    result.push(finalItem);

    const rowItems = rowMap.get(assignedRow) ?? [];
    rowMap.set(assignedRow, [...rowItems, finalItem]);
  }

  return result;
};

export const getMaxSubRow = (items: TimelineItemData[]): number => {
  if (items.length === 0) {
    return 0;
  }
  return Math.max(...items.map((item) => item.subRow));
};

export const clampTime = (time: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, time));
};

export const calculateDraggedTimes = (
  originalStartTime: number,
  originalEndTime: number,
  deltaPixels: number,
  pixelsPerSecond: number
): {startTime: number; endTime: number} => {
  const deltaTime = pixelsToTime(deltaPixels, pixelsPerSecond);
  const duration = originalEndTime - originalStartTime;

  let newStartTime = originalStartTime + deltaTime;
  let newEndTime = originalEndTime + deltaTime;

  if (newStartTime < 0) {
    newStartTime = 0;
    newEndTime = duration;
  }

  return {
    startTime: newStartTime,
    endTime: newEndTime,
  };
};

export const calculateResizedTimes = (
  originalStartTime: number,
  originalEndTime: number,
  deltaPixels: number,
  pixelsPerSecond: number,
  handle: 'left' | 'right',
  maxDuration: number,
  minDuration: number = MIN_ITEM_DURATION
): {startTime: number; endTime: number} => {
  const deltaTime = pixelsToTime(deltaPixels, pixelsPerSecond);

  let newStartTime = originalStartTime;
  let newEndTime = originalEndTime;

  if (handle === 'left') {
    newStartTime = originalStartTime + deltaTime;
    if (newEndTime - newStartTime < minDuration) {
      newStartTime = newEndTime - minDuration;
    }
    newStartTime = Math.max(0, newStartTime);
  } else {
    newEndTime = originalEndTime + deltaTime;
    if (newEndTime - newStartTime < minDuration) {
      newEndTime = newStartTime + minDuration;
    }
    newEndTime = Math.min(maxDuration, newEndTime);
  }

  return {
    startTime: newStartTime,
    endTime: newEndTime,
  };
};

export const formatTime = (seconds: number, showDecimals = true): string => {
  return formatSecondsToTimeString(seconds, {showDecimals});
};

export const generateRulerTicks = (
  duration: number,
  pixelsPerSecond: number
): {position: number; time: number; isMajor: boolean}[] => {
  const ticks: {position: number; time: number; isMajor: boolean}[] = [];

  let interval = 1;
  if (pixelsPerSecond < 40) {
    interval = 5;
  } else if (pixelsPerSecond < 60) {
    interval = 2;
  } else if (pixelsPerSecond > 120) {
    interval = 0.5;
  }

  // Use index-based iteration instead of floating-point accumulation to avoid
  // precision drift (e.g. 0.5 * n can drift from exact values at large n).
  const tickCount = Math.floor(duration / interval);
  for (let i = 0; i <= tickCount; i++) {
    const time = i * interval;
    ticks.push({
      position: timeToPixels(time, pixelsPerSecond),
      time,
      isMajor: i % 5 === 0,
    });
  }

  return ticks;
};
